import { ICameraService } from './interfaces/ICameraService';
import { IDetectionService } from './interfaces/IDetectionService';
import { IStateManager } from './interfaces/IStateManager';
import { IEventBus } from './interfaces/IEventBus';
import { ITracingService } from './interfaces/ITracingService';
import { IMetricsService } from './interfaces/IMetricsService';

import { EventBusService } from './EventBusService';
import { StateManagerService } from './StateManagerService';
import { CameraService } from './CameraService';
import { DetectionService } from './DetectionService';
import { TracingService } from './TracingService';
import { MetricsService } from './MetricsService';

export interface ComponentConfig {
  debug: boolean;
  alignmentTolerance: number;
  maskSize: number;
  cropMargin: number;
  useDocumentClassification: boolean;
  useDocumentDetector: boolean;
  preferredCamera: 'auto' | 'front' | 'back';
  captureDelay: number;
  telemetryCollectorUrl?: string;
  metricsCollectorUrl?: string;
  enableTelemetry?: boolean;
  enableMetrics?: boolean;
  customerId?: string;
  tenantId?: string;
  environment?: string;
  propagateTraceHeaderCorsUrls?: (string | RegExp)[];
  metricsExportIntervalMillis?: number;
}

export class ServiceContainer {
  private services = new Map<string, any>();
  private cleanedUp = false;

  constructor(config: ComponentConfig) {
    this.initializeServices(config);
  }

  private initializeServices(config: ComponentConfig): void {
    // Initialize services in dependency order
    const eventBus = new EventBusService();
    const stateManager = new StateManagerService(eventBus);
    const cameraService = new CameraService(eventBus, config.preferredCamera);
    const detectionService = new DetectionService(
      config.debug,
      config.useDocumentClassification,
      config.useDocumentDetector
    );

    // Initialize tracing service if enabled
    let tracingService: ITracingService | null = null;
    if (config.enableTelemetry !== false) {
      tracingService = new TracingService({
        collectorUrl: config.telemetryCollectorUrl || 'https://collector.jaak.ai/v1/traces',
        serviceName: 'jaak-stamps-webcomponent',
        serviceVersion: '2.1.0',
        debug: config.debug,
        customerId: config.customerId,
        tenantId: config.tenantId,
        environment: config.environment || 'production',
        propagateTraceHeaderCorsUrls: config.propagateTraceHeaderCorsUrls || [],
        enableAutoInstrumentation: true,
      });
      tracingService.initialize();
    }

    // Initialize metrics service if enabled
    let metricsService: IMetricsService | null = null;
    if (config.enableMetrics !== false) {
      metricsService = new MetricsService({
        collectorUrl: config.metricsCollectorUrl || 'https://collector.jaak.ai/v1/metrics',
        serviceName: 'jaak-stamps-webcomponent',
        serviceVersion: '2.1.0',
        debug: config.debug,
        customerId: config.customerId,
        tenantId: config.tenantId,
        environment: config.environment || 'production',
        exportIntervalMillis: config.metricsExportIntervalMillis || 60000,
      });
      metricsService.initialize();
    }

    // Register services
    this.services.set('eventBus', eventBus);
    this.services.set('stateManager', stateManager);
    this.services.set('cameraService', cameraService);
    this.services.set('detectionService', detectionService);
    if (tracingService) {
      this.services.set('tracingService', tracingService);
    }
    if (metricsService) {
      this.services.set('metricsService', metricsService);
    }
  }

  get<T>(serviceName: string): T {
    const service = this.services.get(serviceName);
    if (!service) {
      throw new Error(`Service ${serviceName} not found`);
    }
    return service as T;
  }


  getEventBus(): IEventBus {
    return this.get<IEventBus>('eventBus');
  }

  getStateManager(): IStateManager {
    return this.get<IStateManager>('stateManager');
  }

  getCameraService(): ICameraService {
    return this.get<ICameraService>('cameraService');
  }

  getDetectionService(): IDetectionService {
    return this.get<IDetectionService>('detectionService');
  }

  getTracingService(): ITracingService | null {
    try {
      return this.get<ITracingService>('tracingService');
    } catch {
      return null;
    }
  }

  getMetricsService(): IMetricsService | null {
    try {
      return this.get<IMetricsService>('metricsService');
    } catch {
      return null;
    }
  }

  updateConfig(config: Partial<ComponentConfig>): void {
    // Reinitialize services that depend on configuration changes
    if (config.preferredCamera !== undefined || 
        config.useDocumentClassification !== undefined ||
        config.useDocumentDetector !== undefined) {
      // Would need to reinitialize specific services
      // For simplicity, this is omitted in this example
    }
  }

  async cleanup(): Promise<void> {
    if (this.cleanedUp) {
      return;
    }
    this.cleanedUp = true;

    // Cleanup services in reverse order
    this.getDetectionService().cleanup();

    // Cleanup metrics service if present
    const metricsService = this.getMetricsService();
    if (metricsService) {
      await metricsService.cleanup();
    }

    // Cleanup tracing service if present
    const tracingService = this.getTracingService();
    if (tracingService) {
      await tracingService.cleanup();
    }

    this.getEventBus().clear();
    this.services.clear();
  }
}